import React, { useEffect, useState } from "react"
import { Theme, makeStyles } from "@material-ui/core"
import { useSnackbar } from "notistack"
import { useTranslation } from "react-i18next"
import ChatSidebar from "../chat/components/ChatSidebar"
import ChatWindow from "../chat/components/ChatWindow"
import { useAppContext } from "../app-context/use-app-context"
import CreateChatDialog from "../chat/components/CreateChatDialog"
import { useChatRouteData } from "../chat/hooks/use-chat-route-data"
import { useAuthUserMembership } from "../memberships/hooks/use-auth-user-membership"
import { useRouter } from "../hooks"
import { useIsSmallScreen } from "../hooks/use-is-small-screen"

const ChatRoute = () => {
  const classes = useStyles()
  const { appContext } = useAppContext()
  const { mainContext } = appContext
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation("chat")
  const router = useRouter()
  const query = router.query as { chatId?: string }

  const orgId = mainContext && mainContext.type === "org" ? mainContext.id : ""

  // The URL is the selection: a chat link can be pasted or bookmarked, and back and forward
  // move between the chats that were opened.
  const selectedChatId = query.chatId ?? null

  const selectChat = (chatId: string | null, replace = false) => {
    const params = new URLSearchParams(router.location.search)

    if (chatId) params.set("chatId", chatId)
    else params.delete("chatId")

    // Keeps the context params the rest of the app puts on every route.
    router[replace ? "replace" : "push"]({ search: params.toString() })
  }

  const isSmallScreen = useIsSmallScreen()
  // One pane at a time on phones: the list until a chat is picked, then that chat.
  const showSidebar = !isSmallScreen || !selectedChatId
  const showWindow = !isSmallScreen || Boolean(selectedChatId)

  const [createOpen, setCreateOpen] = useState(false)
  const [search, setSearch] = useState("")
  const { chats, sidebarChats, isLoading, isError, isSuccess, isFetching } = useChatRouteData(search)
  const authUserMembership = useAuthUserMembership(orgId)
  const canCreateChat = Boolean(authUserMembership?.canCreateChat)

  // A chat that is gone (deleted here, or one we were removed from) leaves a dead id in the URL.
  // Replace rather than push, so Back does not land on the dead chat and bounce forward again.
  // Not while fetching: a just-created chat is selected before the invalidated list has refetched,
  // and the stale list would read as "this chat is gone" and drop it from the URL again.
  useEffect(() => {
    if (isSuccess && !isFetching && selectedChatId && !chats.some((chat) => chat.id === selectedChatId))
      selectChat(null, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isFetching, chats, selectedChatId])

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("loadChatsErrorMessage"), {
        variant: "error",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError])

  return (
    <div className={classes.root}>
      <ChatSidebar
        chats={sidebarChats}
        loading={isLoading}
        hidden={!showSidebar}
        onSearch={setSearch}
        onSelectChat={selectChat}
        selectedChatId={selectedChatId}
        canCreateChat={canCreateChat}
        onCreate={() => setCreateOpen(true)}
      />

      <CreateChatDialog
        open={createOpen}
        orgId={orgId}
        onClose={() => setCreateOpen(false)}
        onCreated={(chatId) => {
          selectChat(chatId)
          setCreateOpen(false)
        }}
      />

      {showWindow && (
        <ChatWindow chats={chats} chatId={selectedChatId} onBack={isSmallScreen ? () => selectChat(null) : undefined} />
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    height: "calc(100vh - 72px)",
    [theme.breakpoints.down("xs")]: {
      height: "calc(100vh - 64px)",
    },
    background: theme.palette.background.default,
  },
}))

export default ChatRoute
